import React from "react";

// Comments and review notes are written and stored as plain text, so a URL
// pasted into one renders as inert characters — the reader has to select it
// and paste it into a browser. This turns those URLs into real links without
// touching how the text is authored or stored: a string goes in and React
// elements come out, so React's own escaping still covers everything that is
// not a link. No dangerouslySetInnerHTML anywhere near user input (MGDWD-223).
//
// This stays the renderer for plain-text bodies even after the WYSIWYG editor
// lands (MGDWD-148): the format will be recorded per row rather than guessed,
// so everything written before the editor remains text forever. Guessing from
// content is not an option — real notes contain things like "<sigh>", which a
// sanitiser would silently eat.
const URL_PATTERN = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;

const SENTENCE_PUNCTUATION = ".,;:!?\"'";
const CLOSERS = { ")": "(", "]": "[", "}": "{" };

// A URL at the end of a sentence swallows the punctuation that follows it:
// "grab it at https://mygolfdna.com/offer." must not link the full stop. A
// closing bracket is only punctuation when the URL did not open it, so a link
// that legitimately ends in ")" survives.
export function splitTrailingPunctuation(raw) {
	let end = raw.length;
	while (end > 0) {
		const char = raw[end - 1];
		if (SENTENCE_PUNCTUATION.includes(char)) {
			end -= 1;
			continue;
		}
		const opener = CLOSERS[char];
		if (opener) {
			const body = raw.slice(0, end);
			const opened = body.split(opener).length - 1;
			const closed = body.split(char).length - 1;
			if (closed > opened) {
				end -= 1;
				continue;
			}
		}
		break;
	}
	return [raw.slice(0, end), raw.slice(end)];
}

function TextWithLinks({ text }) {
	if (!text) {
		return null;
	}

	// String.split with a single capture group puts the matched URLs at the
	// odd indexes and the surrounding text at the even ones.
	const parts = String(text).split(URL_PATTERN);

	return (
		<>
			{parts.map((part, index) => {
				if (index % 2 === 0) {
					return part;
				}
				const [url, tail] = splitTrailingPunctuation(part);
				const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
				return (
					<React.Fragment key={index}>
						{/* Some of these bodies sit inside a clickable row — the
						    review list navigates on click. Without stopping
						    propagation the link would open AND the row would
						    navigate. A click on a link is never a click on
						    whatever contains it. */}
						<a
							href={href}
							target="_blank"
							rel="noopener noreferrer"
							onClick={(e) => e.stopPropagation()}
						>
							{url}
						</a>
						{tail}
					</React.Fragment>
				);
			})}
		</>
	);
}

export default TextWithLinks;
